import React from "react";
import FormField from "../../../common/FormField/FormField";
import { Formik } from "formik";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { navigate } from "@reach/router";
import { Popper } from "@material-ui/core";
import { renameValidationSchema } from "../../../../helpers/validationSchema";
import {
  StyledForm,
  FormLabel,
  ButtonGroup,
  BackButton,
  SubmitButton,
} from "./styles";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import {
  Card,
  FormGrid,
  Label,
  PageWrapper,
  Section,
  StyledAutocomplete,
} from "./SplitAndUpload";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";

PDFJS.GlobalWorkerOptions.isEvalSupported = false;

PDFJS.GlobalWorkerOptions.workerSrc = workerSrc;

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const Rename = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        // const errorMessage = "Invalid File Type";
        // if (!fileTypes.includes(file?.type)) throw errorMessage;
        if (accept === ".xlsx") {
          const response = await readExcelFile(file, fileName, setFieldValue);
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        } else if (accept === ".zip") {
          setFieldValue(field, file);
          setFieldValue(fileName, file.name);
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  const inputs = [
    {
      name: "postInputText",
      type: "text",
      label: "Post Name",
    },
  ];

  const upload_templates = [
    {
      name: "excelFile",
      label: "Upload Xlsx",
      type: "upload",
      required: true,
      fileName: "excel",
      accept: ".xlsx",
      types: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
    },
    {
      name: "zipFilePath",
      label: "Zip File (zip)",
      type: "upload",
      required: true,
      fileName: "zip",
      accept: ".zip",
      types: ["application/zip"],
    },
  ];

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "downloading..." });
      const response = await processZipFile(
        values?.excelFile,
        values?.zipFilePath,
        values?.preInputText,
        values?.postInputText,
      );
      const fileName = values?.zip.replace(/\.zip$/, "");
      const url = URL.createObjectURL(response);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}_Output.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      resetForm();
    } catch (error) {
      enqueueSnackbar(error?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    const headerRow = data[0];
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
    ];

    if (!Array.isArray(headerRow) || headerRow.length === 0) {
      enqueueSnackbar("Excel file is missing a valid header row.", {
        variant: "error",
      });
      return [];
    }

    const contractNumberIndex = headerRow.findIndex(
      (header) => header && possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
      return [];
    }
    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  /**
   * @description unzip file
   * @param {*} ids
   * @param {*} zipFile
   * @returns
   */

  const processZipFile = async (
    contractEntries,
    zipFile,
    preInputText,
    postInputText,
    enqueueSnackbar,
  ) => {
    if (!zipFile || contractEntries.length === 0) {
      enqueueSnackbar("Please upload a ZIP file and enter Contract Numbers.", {
        variant: "error",
      });
      return;
    }

    try {
      const zip = new JSZip();
      const loadedZip = await zip.loadAsync(zipFile);
      const outputZip = new JSZip();

      const entries = contractEntries.map((entry) => {
        const [contract, name = ""] = String(entry)
          .split("_")
          .map((s) => s.trim());
        return { contract, name };
      });

      for (const [filename, fileData] of Object.entries(loadedZip.files)) {
        if (filename.endsWith(".pdf") && !fileData.dir) {
          const pdfData = await fileData.async("arraybuffer");
          if (!pdfData || pdfData.byteLength === 0) {
            console.warn(`Skipping empty PDF: ${filename}`);
            continue;
          }

          let pdfTextRaw;
          try {
            pdfTextRaw = await extractTextFromPDF(pdfData);
          } catch (err) {
            console.warn(
              `Failed to extract text from ${filename}:`,
              err.message,
            );
            continue;
          }

          const pdfText = pdfTextRaw.toLowerCase();
          const cleanedPdfText = pdfText.replace(/\s+/g, "");

          const matched = entries.find(({ contract, name }) => {
            return (
              cleanedPdfText.includes(contract.toLowerCase()) &&
              (name === "" || cleanedPdfText.includes(name.toLowerCase()))
            );
          });

          const newFilename = matched
            ? `${preInputText}_${matched.contract}${
                matched.name ? `_${matched.name}` : ""
              }_${postInputText}.pdf`
            : `_${filename}`;

          outputZip.file(newFilename, pdfData);
        }
      }

      const zipBlob = await outputZip.generateAsync({ type: "blob" });
      return zipBlob;
    } catch (error) {
      console.error("Error processing ZIP:", error);
      enqueueSnackbar("Error processing the ZIP file.", { variant: "error" });
    }
  };

  /**
   * @description Extarct text from pdf
   * @param {*} pdfData
   * @returns
   */

  const extractTextFromPDF = async (pdfData) => {
    const loadingTask = PDFJS.getDocument({ data: pdfData });
    const pdf = await loadingTask.promise;
    let text = "";

    for (let i = 0; i < pdf.numPages; i++) {
      const page = await pdf.getPage(i + 1);
      const content = await page.getTextContent();
      const pageText = content.items.map((item) => item.str).join(" ");
      text += pageText + " ";
    }

    return text.trim();
  };

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            preInputText: "",
            postInputText: "",
            excelFile: "",
            zipFilePath: "",
            excel: "",
            zip: "",
          }}
          validationSchema={renameValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
            resetForm,
          }) => (
            <StyledForm>
              <Section>
                <FormGrid>
                  <div>
                    <Label>
                      Pre Name <span className="required-star"> *</span>
                    </Label>
                    <StyledAutocomplete
                      id="preInputText-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            onBlur={handleBlur}
                            error={
                              errors["preInputText"] && touched["preInputText"]
                            }
                            helperText={
                              touched["preInputText"] && errors["preInputText"]
                            }
                            placeholder="Select pre name..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: COLORS.COLOR_DARK,
                              fontFamily: theme.fonts.primaryFontSemiBold,
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: COLORS.INPUT_LABEL,
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("preInputText", newValue?.name || "")
                      }
                    />
                  </div>
                  {inputs.map((input, index) => (
                    <FormField
                      key={index}
                      {...{
                        input,
                        values,
                        handleBlur,
                        handleChange,
                        touched,
                        errors,
                      }}
                    />
                  ))}
                </FormGrid>
              </Section>
              <Section>
                <FormGrid className="mis-dashboard">
                  {upload_templates.map((upload, index) => (
                    <div style={{ marginTop: 10 }}>
                      <FormLabel>
                        {upload.label} <span className="required-star"> *</span>
                      </FormLabel>
                      <CustomFileUpload
                        error={
                          touched[upload.fileName] && errors[upload.fileName]
                        }
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.types,
                            upload.accept,
                            values,
                            upload.fileName,
                          )
                        }
                        style={{
                          width: "100%",
                        }}
                        accept={upload.accept}
                        file={{ name: values[upload.fileName] }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>
              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default Rename;
